import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { NgxPaginationModule } from 'ngx-pagination';
import { LoginApiService } from 'src/app/service/login/login-api.service';
import { ThemeService } from 'src/app/service/theme/theme.service';
import { DataStoreService } from 'src/app/utils/data_store/data-store.service';
import { RouteLinksByRolesService } from 'src/app/utils/route_links/route-links-by-roles.service';

// Typing interface for links
interface RouteLink {
  link: string;
  name: string;
  icon: string;
}

@Component({
  selector: 'app-nav',
  standalone: true,
  imports: [
    CommonModule,
    NgxPaginationModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
  ],
  templateUrl: './nav.component.html',
  styleUrls: ['./nav.component.css'],
})
export class NavComponent implements OnInit {
  // Constructorless dependency injections
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly api = inject(LoginApiService);
  private readonly dataStore = inject(DataStoreService);
  private readonly routeLinksService = inject(RouteLinksByRolesService);
  private readonly themeService = inject(ThemeService);
  private readonly destroyRef = inject(DestroyRef);

  // Link global theme service signal directly to template reference
  readonly isDarkMode = this.themeService.isDarkMode;

  // Strongly typed local signals for lightning-fast view rendering
  readonly isUploading = signal<boolean>(false);
  readonly showNewPassword = signal<boolean>(false);
  readonly showConfirmPassword = signal<boolean>(false);
  readonly isError = signal<boolean>(false);
  readonly errorMessage = signal<string>('');

  // Domain states
  public links: RouteLink[] = [];
  login_user = 'User';
  changePasswordForm!: FormGroup;

  ngOnInit(): void {
    this.initializeForm();
    this.assignNavigationLinks();
    this.login_user = this.dataStore.getLoginName() ?? 'User';
  }

  private initializeForm(): void {
    this.changePasswordForm = this.fb.group({
      newPassword: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', [Validators.required]],
    }, { validators: this.passwordMatchValidator });
  }

  private passwordMatchValidator(g: FormGroup) {
    return g.get('newPassword')?.value === g.get('confirmPassword')?.value
      ? null : { mismatch: true };
  }

  /**
   * Evaluates and builds structural link assignments mapped out by roles
   */
  private assignNavigationLinks(): void {
    const role = this.dataStore.getRole();

    // Works perfectly if this returns 'ADMIN' or ['ADMIN', 'LUCKY_DRAW_ADMIN']
    const activeRoles = this.dataStore.getRole();

    // Evaluates multiple permissions simultaneously with no extra logic needed here!
    this.links = this.routeLinksService.getLinksForRole(activeRoles) ?? [];

  }

  /**
   * Delegates dark mode mutation down to global context architecture
   */
  toggleDarkMode(): void {
    this.themeService.toggleTheme();
  }

  submitAdminLogout(): void {
    this.isUploading.set(true);

    this.api.getLogout()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          sessionStorage.clear();
          localStorage.removeItem('token'); // Clear security signatures cleanly
        },
        error: (err: any) => {
          this.isUploading.set(false);
          console.error('Logout protocol failure:', err);
          alert(err?.message || 'Network disruption during logout.');
        },
        complete: () => {
          this.isUploading.set(false);
          this.router.navigate(['/login']).then(() => {
            window.location.reload();
          });
        },
      });
  }

  submitChangePassword(): void {
    if (this.changePasswordForm.invalid) {
      this.isError.set(true);
      this.errorMessage.set('Please fill out all criteria accurately.');
      return;
    }

    const newPass = this.changePasswordForm.get('newPassword')?.value;
    this.isError.set(false);
    this.updatePassword(newPass);
  }

  private updatePassword(newPassword: string): void {
    this.isUploading.set(true);

    this.api.getUpdatePassword(this.login_user, newPassword)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          const authorization = 'Basic ' + window.btoa(`${this.login_user}:${newPassword}`);
          this.dataStore.setAuth(authorization);
          this.changePasswordForm.reset();
          alert('Security settings successfully upgraded.');
        },
        error: (err: any) => {
          this.isError.set(true);
          this.errorMessage.set(err?.message || 'Failed to apply new authorization profile.');
          this.isUploading.set(false);
        },
        complete: () => {
          this.isUploading.set(false);
          this.isError.set(false);
          this.errorMessage.set('');

          // Clean data bootstrap modal closing hook safely using class list abstractions instead of legacy jQuery
          const modalElement = document.getElementById('changePasswordModal');
          if (modalElement) {
            (window as any).bootstrap?.Modal?.getInstance(modalElement)?.hide();
          }
        },
      });
  }
}
